import React, { useRef, useState, useEffect } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper'

import 'swiper/css'
import 'swiper/css/pagination'

import whySafari1 from '../../../assets/assets-safari/why/1.jpg'
import whySafari2 from '../../../assets/assets-safari/why/2.jpg'
import whySafari3 from '../../../assets/assets-safari/why/3.jpg'
import whySafari4 from '../../../assets/assets-safari/why/4.jpg'
import whySafari5 from '../../../assets/assets-safari/why/5.jpg'
import whySafari6 from '../../../assets/assets-safari/why/6.jpg'

type CardData = {
    image: string
    key: string
    hasBadge?: boolean
}

const cards: CardData[] = [
    { image: whySafari1, key: 'wildlife' },
    { image: whySafari2, key: 'access' },
    { image: whySafari3, key: 'experience', hasBadge: true },
    { image: whySafari4, key: 'length' },
    { image: whySafari5, key: 'lodge', hasBadge: true },
    { image: whySafari6, key: 'conservation' },
]

const CardContent: React.FC<{ card: CardData }> = ({ card }) => {
    const { t, i18n } = useTranslation('safari')
    const isRu = i18n.language === 'ru';
    const title = t(`whyChoosing.cards.${card.key}.title`)
    const description = t(`whyChoosing.cards.${card.key}.description`)
    const badge = card.hasBadge ? t(`whyChoosing.cards.${card.key}.badge`) : ''
    return (
        <>
            <img src={card.image} alt={title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

            <div className="relative h-full flex flex-col justify-between">
                <div>
                    {badge && (
                        <span className="leading-none w-fit inline-flex items-center h-5 sm:h-8 lg:h-[42px] px-2.5 text-white text-[10px] sm:text-xs lg:text-lg rounded-[4.4px] sm:rounded-lg lg:rounded-[10px] bg-[#4A90A4]">
                            {badge}
                        </span>
                    )}
                </div>

                <div className="sm:min-h-[94px]">
                    <h3 className="lg:text-[22px] sm:text-xl text-sm font-medium text-white leading-tight">
                        {title}
                    </h3>

                    <p className={`${isRu ? 'lg:text-base' : 'lg:text-lg'} sm:text-sm text-xs text-white sm:mt-2.5 mt-1`}>
                        {description}
                    </p>
                </div>
            </div>
        </>
    )
}

const WhyChoosingSafariGrid: React.FC = () => {
    const swiperInstanceRef = useRef<SwiperType | null>(null)
    const sliderContainerRef = useRef<HTMLDivElement | null>(null)
    const [isMobile, setIsMobile] = useState<boolean>(false)
    const { i18n } = useTranslation('safari')
    const isRu = i18n.language === 'ru'

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 1099px)')

        const handleMediaChange = (e: MediaQueryListEvent | MediaQueryList) => {
            setIsMobile(e.matches)
        }

        handleMediaChange(mediaQuery)
        mediaQuery.addEventListener('change', handleMediaChange)

        return () => {
            mediaQuery.removeEventListener('change', handleMediaChange)
        }
    }, [])

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (!swiperInstanceRef.current) return
            const swiper = swiperInstanceRef.current
            const clickedSlide = (e.target as HTMLElement).closest('.swiper-slide')

            if (!clickedSlide) return

            if (clickedSlide.classList.contains('swiper-slide-next')) {
                swiper.slideNext()
            } else if (clickedSlide.classList.contains('swiper-slide-prev')) {
                swiper.slidePrev()
            }
        }

        const sliderElement = sliderContainerRef.current
        if (sliderElement) {
            sliderElement.addEventListener('click', handleClick)
        }

        return () => {
            if (sliderElement) {
                sliderElement.removeEventListener('click', handleClick)
            }
        }
    }, [isMobile])

    return (
        <section className="lg:py-[60px] sm:py-10 py-8 bg-white">
            <div className="max-w-[1600px] lg:px-10 mx-auto">
                <div className="text-center  mb-6 sm:mb-8 lg:mb-10 px-5 sm:px-6 lg:px-10 mx-auto">
                    <h2 className={`section-heading mx-auto ${isRu ? 'max-w-[270px] sm:max-w-full' : ''}`}>
                        <Trans
                            i18nKey="whyChoosing.heading"
                            ns="safari"
                            components={{ br1: <br className='sm:hidden' /> }}
                        />
                    </h2>

                    <p className={`text-xs sm:text-lg text-[#4A4A4A] font-normal mt-2 sm:mt-4 mx-auto leading-[20px] sm:leading-[29px] ${isRu ? 'max-w-[486px] lg:max-w-[830px]' : 'max-w-[640px]'}`}>
                        <Trans
                            i18nKey="whyChoosing.subheading"
                            ns="safari"
                            components={{ br1: <br className='sm:hidden' /> }}
                        />
                    </p>
                </div>

                {isMobile ? (
                    <div ref={sliderContainerRef}>
                        <Swiper
                            onSwiper={(swiper: SwiperType) => { swiperInstanceRef.current = swiper }}
                            modules={[Pagination]}
                            slidesPerView="auto"
                            spaceBetween={window.innerWidth > 640 ? 20 : 7.71}
                            slidesOffsetBefore={20}
                            slidesOffsetAfter={20}
                            initialSlide={0}
                            breakpoints={{
                                1024: { slidesOffsetBefore: 40, slidesOffsetAfter: 40 }
                            }}
                            pagination={{
                                clickable: true,
                                el: '.why-choosing-safari-pagination'
                            }}
                        >
                            {Array.from({ length: Math.ceil(cards.length / 2) }, (_, slideIndex) => {
                                const topCard = cards[slideIndex * 2]
                                const bottomCard = cards[slideIndex * 2 + 1]
                                return (
                                    <SwiperSlide key={slideIndex} className={`sm:!w-[70vw] sm:max-w-[456px] ${isRu ? 'max-w-[300px]' : 'max-w-[290px]'} w-full`}>
                                        <div className="flex flex-col gap-[7.71px] sm:gap-5 w-full">
                                            <div className={`${isRu ? 'h-[200px]' : 'h-[180px]'} sm:h-[256px] rounded-lg sm:rounded-[20px] py-4 px-3 sm:!p-5 relative overflow-hidden`}>
                                                <CardContent card={topCard} />
                                            </div>

                                            {bottomCard && (
                                                <div className={`${isRu ? 'h-[200px]' : 'h-[180px]'} sm:h-[256px] rounded-lg sm:rounded-[20px] py-4 px-3 sm:!p-5 relative overflow-hidden`}>
                                                    <CardContent card={bottomCard} />
                                                </div>
                                            )}
                                        </div>
                                    </SwiperSlide>
                                )
                            })}
                        </Swiper>

                        <div className="pagination why-choosing-safari-pagination mt-8" />
                    </div>
                ) : (
                    <div className="grid grid-cols-3 gap-5">
                        {cards.map((card, index) => (
                            <div
                                key={index}
                                className="h-[320px] rounded-[20px] p-[27px] relative overflow-hidden"
                            >
                                <CardContent card={card} />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    )
}

export default WhyChoosingSafariGrid
